// Official Nad.fun Contract ABIs
// Source: https://github.com/Naddotfun/contract-v3-abi

// ========================================
// Contract Addresses - Monad Mainnet
// ========================================
export const NADFUN_CONTRACTS = {
    DEX_FACTORY: '0x6B5F564339DbAD6b780249827f2198a841FEB7F3',
    WMON: '0x3bd359C1119dA7Da1D913D1C4D2B7c461115433A',
    BONDING_CURVE_ROUTER: '0x6F6B8F1a20703309951a5127c45B49b1CD981A22',
    BONDING_CURVE: '0xA7283d07812a02AFB7C09B60f8896bCEA3F90aCE',
    DEX_ROUTER: '0x0B79d71AE99528D1dB24A4148b5f4F865cc2b137',
    LENS: '0x7e78A8DE94f21804F7a17F4E8BF9EC2c872187ea',
} as const;

// ========================================
// ERC-20 Token Standard ABI
// ========================================
export const ERC20_ABI = [
    {
        inputs: [{ name: 'account', type: 'address' }],
        name: 'balanceOf',
        outputs: [{ name: '', type: 'uint256' }],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'decimals',
        outputs: [{ name: '', type: 'uint8' }],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'name',
        outputs: [{ name: '', type: 'string' }],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'symbol',
        outputs: [{ name: '', type: 'string' }],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [],
        name: 'totalSupply',
        outputs: [{ name: '', type: 'uint256' }],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            { name: 'owner', type: 'address' },
            { name: 'spender', type: 'address' },
        ],
        name: 'allowance',
        outputs: [{ name: '', type: 'uint256' }],
        stateMutability: 'view',
        type: 'function',
    },
    {
        inputs: [
            { name: 'spender', type: 'address' },
            { name: 'amount', type: 'uint256' },
        ],
        name: 'approve',
        outputs: [{ name: '', type: 'bool' }],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            { name: 'to', type: 'address' },
            { name: 'amount', type: 'uint256' },
        ],
        name: 'transfer',
        outputs: [{ name: '', type: 'bool' }],
        stateMutability: 'nonpayable',
        type: 'function',
    },
    {
        inputs: [
            { name: 'from', type: 'address' },
            { name: 'to', type: 'address' },
            { name: 'amount', type: 'uint256' },
        ],
        name: 'transferFrom',
        outputs: [{ name: '', type: 'bool' }],
        stateMutability: 'nonpayable',
        type: 'function',
    },
] as const;

// ========================================
// Nad.fun Lens Contract ABI (Price Queries & Token Info)
// Use for ALL read operations - single interface for everything
// ========================================
export const LENS_ABI = [
    {
        type: 'function',
        name: 'availableBuyTokens',
        inputs: [{ name: '_token', type: 'address', internalType: 'address' }],
        outputs: [
            { name: 'availableBuyToken', type: 'uint256', internalType: 'uint256' },
            { name: 'requiredMonAmount', type: 'uint256', internalType: 'uint256' },
        ],
        stateMutability: 'view',
    },
    {
        type: 'function',
        name: 'getAmountIn',
        inputs: [
            { name: '_token', type: 'address', internalType: 'address' },
            { name: '_amountOut', type: 'uint256', internalType: 'uint256' },
            { name: '_isBuy', type: 'bool', internalType: 'bool' },
        ],
        outputs: [
            { name: 'router', type: 'address', internalType: 'address' },
            { name: 'amountIn', type: 'uint256', internalType: 'uint256' },
        ],
        stateMutability: 'view',
    },
    {
        type: 'function',
        name: 'getAmountOut',
        inputs: [
            { name: '_token', type: 'address', internalType: 'address' },
            { name: '_amountIn', type: 'uint256', internalType: 'uint256' },
            { name: '_isBuy', type: 'bool', internalType: 'bool' },
        ],
        outputs: [
            { name: 'router', type: 'address', internalType: 'address' },
            { name: 'amountOut', type: 'uint256', internalType: 'uint256' },
        ],
        stateMutability: 'view',
    },
    {
        type: 'function',
        name: 'getInitialBuyAmountOut',
        inputs: [{ name: '_amountIn', type: 'uint256', internalType: 'uint256' }],
        outputs: [{ name: 'amountOut', type: 'uint256', internalType: 'uint256' }],
        stateMutability: 'view',
    },
    {
        type: 'function',
        name: 'getProgress',
        inputs: [{ name: '_token', type: 'address', internalType: 'address' }],
        outputs: [{ name: 'progress', type: 'uint256', internalType: 'uint256' }],
        stateMutability: 'view',
    },
    {
        type: 'function',
        name: 'isGraduated',
        inputs: [{ name: '_token', type: 'address', internalType: 'address' }],
        outputs: [{ name: 'isGraduated', type: 'bool', internalType: 'bool' }],
        stateMutability: 'view',
    },
    {
        type: 'function',
        name: 'isLocked',
        inputs: [{ name: '_token', type: 'address', internalType: 'address' }],
        outputs: [{ name: 'isLocked', type: 'bool', internalType: 'bool' }],
        stateMutability: 'view',
    },
] as const;

// ========================================
// Nad.fun Bonding Curve Router ABI (Trading before graduation)
// ========================================
export const BONDING_CURVE_ROUTER_ABI = [
    {
        type: 'function',
        name: 'availableBuyTokens',
        inputs: [{ name: 'token', type: 'address', internalType: 'address' }],
        outputs: [
            { name: 'availableBuyToken', type: 'uint256', internalType: 'uint256' },
            { name: 'requiredMonAmount', type: 'uint256', internalType: 'uint256' },
        ],
        stateMutability: 'view',
    },
    {
        type: 'function',
        name: 'buy',
        inputs: [
            {
                name: 'params',
                type: 'tuple',
                internalType: 'struct IBondingCurveRouter.BuyParams',
                components: [
                    { name: 'amountOutMin', type: 'uint256', internalType: 'uint256' },
                    { name: 'token', type: 'address', internalType: 'address' },
                    { name: 'to', type: 'address', internalType: 'address' },
                    { name: 'deadline', type: 'uint256', internalType: 'uint256' },
                ],
            },
        ],
        outputs: [],
        stateMutability: 'payable',
    },
    {
        type: 'function',
        name: 'create',
        inputs: [
            {
                name: 'params',
                type: 'tuple',
                internalType: 'struct IBondingCurveRouter.TokenCreationParams',
                components: [
                    { name: 'name', type: 'string', internalType: 'string' },
                    { name: 'symbol', type: 'string', internalType: 'string' },
                    { name: 'tokenURI', type: 'string', internalType: 'string' },
                    { name: 'amountOut', type: 'uint256', internalType: 'uint256' },
                    { name: 'salt', type: 'bytes32', internalType: 'bytes32' },
                    { name: 'actionId', type: 'uint8', internalType: 'uint8' },
                ],
            },
        ],
        outputs: [
            { name: 'token', type: 'address', internalType: 'address' },
            { name: 'pool', type: 'address', internalType: 'address' },
        ],
        stateMutability: 'payable',
    },
    {
        type: 'function',
        name: 'curve',
        inputs: [],
        outputs: [{ name: '', type: 'address', internalType: 'address' }],
        stateMutability: 'view',
    },
    {
        type: 'function',
        name: 'exactOutBuy',
        inputs: [
            {
                name: 'params',
                type: 'tuple',
                internalType: 'struct IBondingCurveRouter.ExactOutBuyParams',
                components: [
                    { name: 'amountInMax', type: 'uint256', internalType: 'uint256' },
                    { name: 'amountOut', type: 'uint256', internalType: 'uint256' },
                    { name: 'token', type: 'address', internalType: 'address' },
                    { name: 'to', type: 'address', internalType: 'address' },
                    { name: 'deadline', type: 'uint256', internalType: 'uint256' },
                ],
            },
        ],
        outputs: [],
        stateMutability: 'payable',
    },
    {
        type: 'function',
        name: 'sell',
        inputs: [
            {
                name: 'params',
                type: 'tuple',
                internalType: 'struct IBondingCurveRouter.SellParams',
                components: [
                    { name: 'amountIn', type: 'uint256', internalType: 'uint256' },
                    { name: 'amountOutMin', type: 'uint256', internalType: 'uint256' },
                    { name: 'token', type: 'address', internalType: 'address' },
                    { name: 'to', type: 'address', internalType: 'address' },
                    { name: 'deadline', type: 'uint256', internalType: 'uint256' },
                ],
            },
        ],
        outputs: [],
        stateMutability: 'nonpayable',
    },
    {
        type: 'function',
        name: 'getAmountOutWithFee',
        inputs: [
            { name: 'token', type: 'address', internalType: 'address' },
            { name: 'amountIn', type: 'uint256', internalType: 'uint256' },
            { name: 'isBuy', type: 'bool', internalType: 'bool' },
        ],
        outputs: [{ name: 'amountOut', type: 'uint256', internalType: 'uint256' }],
        stateMutability: 'view',
    },
    {
        type: 'function',
        name: 'getAmountInWithFee',
        inputs: [
            { name: 'token', type: 'address', internalType: 'address' },
            { name: 'amountOut', type: 'uint256', internalType: 'uint256' },
            { name: 'isBuy', type: 'bool', internalType: 'bool' },
        ],
        outputs: [{ name: 'amountIn', type: 'uint256', internalType: 'uint256' }],
        stateMutability: 'view',
    },
    {
        type: 'function',
        name: 'wMon',
        inputs: [],
        outputs: [{ name: '', type: 'address', internalType: 'address' }],
        stateMutability: 'view',
    },
    { type: 'error', name: 'DeadlineExpired', inputs: [] },
    { type: 'error', name: 'InsufficientAmountIn', inputs: [] },
    { type: 'error', name: 'InsufficientAmountInMax', inputs: [] },
    { type: 'error', name: 'InsufficientAmountOut', inputs: [] },
    { type: 'error', name: 'InsufficientMon', inputs: [] },
    { type: 'error', name: 'InvalidAllowance', inputs: [] },
] as const;

// ========================================
// Nad.fun DEX Router ABI (Trading after graduation)
// ========================================
export const DEX_ROUTER_ABI = [
    {
        type: 'function',
        name: 'buy',
        inputs: [
            {
                name: 'params',
                type: 'tuple',
                components: [
                    { name: 'amountOutMin', type: 'uint256' },
                    { name: 'token', type: 'address' },
                    { name: 'to', type: 'address' },
                    { name: 'deadline', type: 'uint256' },
                ],
            },
        ],
        outputs: [{ name: 'amountOut', type: 'uint256' }],
        stateMutability: 'payable',
    },
    {
        type: 'function',
        name: 'sell',
        inputs: [
            {
                name: 'params',
                type: 'tuple',
                components: [
                    { name: 'amountIn', type: 'uint256' },
                    { name: 'amountOutMin', type: 'uint256' },
                    { name: 'token', type: 'address' },
                    { name: 'to', type: 'address' },
                    { name: 'deadline', type: 'uint256' },
                ],
            },
        ],
        outputs: [{ name: 'amountOut', type: 'uint256' }],
        stateMutability: 'nonpayable',
    },
    {
        type: 'function',
        name: 'exactOutBuy',
        inputs: [
            {
                name: 'params',
                type: 'tuple',
                components: [
                    { name: 'amountInMax', type: 'uint256' },
                    { name: 'amountOut', type: 'uint256' },
                    { name: 'token', type: 'address' },
                    { name: 'to', type: 'address' },
                    { name: 'deadline', type: 'uint256' },
                ],
            },
        ],
        outputs: [{ name: 'amountIn', type: 'uint256' }],
        stateMutability: 'payable',
    },
] as const;
